import { useFetch } from "../../hooks/useFetch";
import { money } from "../../lib/api";
import { AIBar } from "../../components/AIBar";
import { StatCard } from "../../components/StatCard";
import { Loading } from "../../components/States";
import { PageHeader } from "../../components/PageHeader";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ManualDownload } from "../../components/ProgramApplications";
import {
  TrendingUp, FileText, AlertTriangle, CalendarCheck, DollarSign, HardHat, Users, ClipboardX,
} from "lucide-react";

export default function CommandCenter() {
  const { data, loading } = useFetch("/dashboard/owner");
  const { user } = useAuth();
  const navigate = useNavigate();

  if (loading || !data) return <Loading label="Loading Command Center" />;

  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={`${greeting}, ${user?.name?.split(" ")[0] || "Larry"}`}
        title="Command Center"
        subtitle={`You have ${money(data.active_backlog)} in active backlog. ${data.outstanding_bids.count} bids worth ${money(data.outstanding_bids.value)} need follow-up. ${data.over_budget_jobs} jobs are over budget. ${data.upcoming_inspections} inspections this week.`}
      />

      <div className="flex flex-wrap gap-3">
        <button onClick={() => navigate("/app/desk?tab=Jobs%20%26%20Invoices")} className="rounded-lg bg-[#F7C52B] px-4 py-2 text-sm font-bold text-black" data-testid="cc-new-job">+ New Job</button>
        {user?.role === "owner" && <ManualDownload />}
      </div>

      <AIBar />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="kpi-backlog" label="Active Backlog" value={money(data.active_backlog)} hint={`${data.active_jobs} active jobs`} icon={TrendingUp} />
        <StatCard testid="kpi-bids" label="Outstanding Bids" value={money(data.outstanding_bids.value)} hint={`${data.outstanding_bids.count} need follow-up`} icon={FileText} tone="warning" onClick={() => navigate("/app/sales")} />
        <StatCard testid="kpi-receivables" label="Needs Collection" value={money(data.overdue_receivables)} hint="Overdue 30+ days" icon={DollarSign} tone="danger" onClick={() => navigate("/app/money")} />
        <StatCard testid="kpi-inspections" label="Inspections" value={data.upcoming_inspections} hint="Scheduled this week" icon={CalendarCheck} onClick={() => navigate("/app/projects")} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="hw-card p-6 lg:col-span-2 hw-rise">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-head text-xl font-bold text-white">Who owes us money</h3>
            <span className="hw-badge"><DollarSign size={12} /> {money(data.overdue_receivables)} overdue</span>
          </div>
          <div className="space-y-3">
            {data.builders_owe.length === 0 && <p className="text-slate-400">All receivables current.</p>}
            {data.builders_owe.map((b) => (
              <div key={b.name} className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/40 px-4 py-3" data-testid={`owes-${b.name.replace(/\s/g, "-")}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500/15 text-rose-300"><AlertTriangle size={18} /></div>
                  <span className="font-semibold text-white">{b.name}</span>
                </div>
                <span className="font-head text-2xl font-bold text-rose-300">{money(b.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <StatCard testid="kpi-crews" label="Crews Scheduled" value={data.crews_today} hint="Active today" icon={HardHat} />
          <StatCard testid="kpi-changeorders" label="Change Orders" value={data.pending_change_orders} hint="Awaiting signature" icon={ClipboardX} tone="warning" onClick={() => navigate("/app/projects")} />
          <StatCard testid="kpi-overbudget" label="Over Budget" value={data.over_budget_jobs} hint="Jobs exceeding labor budget" icon={Users} tone="danger" onClick={() => navigate("/app/reports")} />
        </div>
      </div>

      <div className="hw-card p-6 hw-rise">
        <h3 className="mb-5 font-head text-xl font-bold text-white">Production pipeline by stage</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          {["rough-in", "service", "trim", "inspection", "final"].map((s) => (
            <div key={s} className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 text-center">
              <div className="font-head text-3xl font-extrabold text-cyan-400">{data.stage_breakdown[s] || 0}</div>
              <div className="mt-1 text-xs font-mono2 uppercase tracking-wide text-slate-500">{s.replace("-", " ")}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
